import AuthService                    from 'services/auth.service';
import React, { useState, useEffect } from 'react';
import UserService                    from 'services/user.service';
import { getCable } from 'services/cable';
import ProfileLayout from './ProfileLayout';
import ProfileNavBar from './ProfileNavBar';
import ProfileSectionCard from './ProfileSectionCard';

import BasicInformation from './BasicInformation';
import EmailRecipients from './EmailRecipients';  
import UpdatesAndPromotions from './UpdatesAndPromotions';
import TeleconsultSettings from './TeleconsultSettings';
import AppointmentReminder from './AppointmentReminder';
import ChangePassword from './ChangePassword';
import EmailAndPhoneNo from './EmailAndPhoneNo';
import PracticeLocations from './PracticeLocations';
import IDVerification from './IDVerification';
import BackgroundInformation from './BackgroundInformation';
import { toast } from 'react-toastify';

const Profile = () => {
  const currentUser                   = AuthService.getCurrentUser();
  const [profile, setProfile]         = useState('');
  const [activeTab, setActiveTab] = useState('general_details');

  const assistantViewDoctorLink       = window.location.search.match(/view_doctor_id=(\d+)/);
  const specificDoctorId              = assistantViewDoctorLink ? assistantViewDoctorLink[1] : null;
  const searchDocId                   = specificDoctorId || currentUser.id

  // Assistant can view profile only, cannot edit
  const isAssistant = currentUser?.user_type === 'assistant';
  const isReadOnly = isAssistant || Boolean(specificDoctorId);

  const fetchProfileData = () => {
    return UserService.profile(searchDocId).then(
      (data) => {
        setProfile(data);
      },
      (error) => {
        if (error.response?.status === 401) {
          AuthService.logout();
          window.location.replace('/eb/login');
        }
      }
    );
  };

  const RefreshProfilePrompt = ({ closeToast }) => (
    <div>
      <p className="mb-2">Your profile change request has been reviewed!</p>
      <button 
        type="button"
        className="refresh-profile-btn" 
        onClick={() => {
          closeToast();       
        }}
      >
        Refresh Profile
      </button>
    </div>
  );

  useEffect(() => {
    fetchProfileData();

    const consumer = getCable();
    const subscription = consumer.subscriptions.create(
      { channel: 'NotificationsChannel', doctor_id: searchDocId},
      {
        connected(){
          console.log('Connected to Notifications Channel');
        },
        disconnected(){
          console.log('Disconnected');
        },
        received(data){
          console.log('Message received:', data);
          let payload = data?.payload;
          if (payload?.type === 'provider_profile__update') {
            toast.info(
              <RefreshProfilePrompt closeToast={() => toast.dismiss()}/>, {
                autoClose: false,
                closeOnClick: false,
                draggable: false,
                onClose: () => fetchProfileData()
              }
            );
          }
        }
      }
    );
    return () => {
      subscription.unsubscribe();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!profile) return <div>Loading Profile...</div>;

  return (
    <div id="main-content-target">
      <h1 className="mb-4 header-title">Profile & Settings</h1>
      <ProfileNavBar activeTab={activeTab} setActiveTab={setActiveTab}/>
      <ProfileLayout>
        {activeTab === 'general_details' && (
          <>
            <BasicInformation onProfileRefresh={fetchProfileData} profile={profile} isReadOnly={isReadOnly}/>
            <IDVerification onProfileRefresh={fetchProfileData} profile={profile} isReadOnly={isReadOnly}/>
          </>
        )}

        {activeTab === 'professional_details' && (
          <>
            <BackgroundInformation onProfileRefresh={fetchProfileData} profile={profile} isReadOnly={isReadOnly}/>
            <PracticeLocations profile={profile}/>
          </>
          
        )}

        {activeTab === 'availability' && (
          <>
            <TeleconsultSettings
              profile={profile}
              searchDocId={searchDocId}
              initialTeleconsultEnabled={profile?.teleconsult_enabled}
              isReadOnly={isReadOnly}
            />
          </>
        )}

        {activeTab === 'sign_in_and_security' && (
          <>
            <EmailAndPhoneNo profile={profile}/>
            <ChangePassword isReadOnly={isReadOnly}/>
          </>
        )}

        {activeTab === 'notifications' && (
          <>
            <ProfileSectionCard title="Preferences" showEditButton={false}>
               <AppointmentReminder
                initialAppointmentReminderEmailEnabled={profile?.appointment_email_reminder}
                isReadOnly={isReadOnly}
              />
              <UpdatesAndPromotions searchDocId={searchDocId}
                initialMarketingEmailEnabled={profile?.marketing_email_enabled}
                isReadOnly={isReadOnly}
              />
            </ProfileSectionCard>

            <EmailRecipients profile={profile} onProfileRefresh={fetchProfileData} isReadOnly={isReadOnly}/>
          </>
        )}
      </ProfileLayout>
    </div>
  );
}

export default Profile;